import { useEffect, useRef } from "react";

interface UseLoadMoreOnIntersectOptions {
  enabled: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
  rootMargin?: string;
}

export function useLoadMoreOnIntersect({
  enabled,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
  rootMargin = "200px",
}: UseLoadMoreOnIntersectOptions) {
  const sentinelRef = useRef<HTMLDivElement>(null);
  const fetchingRef = useRef(isFetchingNextPage);
  fetchingRef.current = isFetchingNextPage;

  useEffect(() => {
    if (!enabled || !hasNextPage || isFetchingNextPage) return;
    const el = sentinelRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry?.isIntersecting || !hasNextPage || fetchingRef.current) return;
        void fetchNextPage();
      },
      { root: null, rootMargin, threshold: 0 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [enabled, hasNextPage, isFetchingNextPage, fetchNextPage, rootMargin]);

  return sentinelRef;
}
